import { FC, MouseEvent, useMemo, useState, useContext } from "react";

import { TrainingFolder } from "@/features/training/model/Training/TrainingFolder";
import { EditTrainingFolder } from "@/features/training/pages/training-tab/panel-left/accordion/EditTrainingFolder";
import { TrainingCourseItem } from "@/features/training/pages/training-tab/panel-left/accordion/TrainingCourseItem";
import { TrainingLinkItem } from "@/features/training/pages/training-tab/panel-left/accordion/TrainingLinkItem";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import {
	Box,
	Chip,
	IconButton,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";

import { GetLanguageValueAsString } from "@/app/application/Translate";


interface TrainingFolderItemProps {
	folder: TrainingFolder;
	i: number;
}

export const TrainingFolderItem: FC<TrainingFolderItemProps> = ({
	folder,
	i,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const [openFolder, setOpenFolder] = useState(false);

	const courses = useMemo(
		() =>
			folder.Courses?.map((c) => (
				<TrainingCourseItem key={c.Id} course={c} />
			)) ?? [],
		[folder.Courses],
	);
	const links = useMemo(
		() =>
			folder.Links?.map((l) => <TrainingLinkItem key={l.Id} link={l} />) ?? [],
		[folder.Links],
	);

	const courseCount = folder.Courses?.length ?? 0;
	const linkCount = folder.Links?.length ?? 0;

	const evenBg = alpha(theme.palette.action.hover, 0.1);

	return (
		<>
			<TreeItem
				itemId={folder.Id ? `folder-${folder.Id}` : folder.Name}
				label={
					<Box
						id={folder.Id != null ? `tree-node-folder-${folder.Id}` : undefined}
						aria-label={`Folder ${folder.Name}`}
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							py: 0.75,
							pr: 1,
							pl: 0.5,
							minWidth: 0,
						}}
					>
						<FolderOutlinedIcon
							fontSize="small"
							sx={{ color: "action.active", mx: 1 }}
						/>

						<Typography
							variant="body2"
							fontWeight={600}
							sx={{
								minWidth: 0,
								flex: 1,
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
							}}
							title={folder.Name}
						>
							{folder.Name}
						</Typography>

						{/* Counts */}
						<Box
							sx={{
								display: "inline-flex",
								alignItems: "center",
								gap: 0.5,
								mr: 0.5,
								flexShrink: 0,
							}}
						>
							{courseCount > 0 && (
								<Chip
									sx={{
										height: 22,
										borderRadius: 1.25,
										"& .MuiChip-label": {
											px: 1,
											fontWeight: 700,
										},
									}}
									size="small"
									variant="outlined"
									icon={<SchoolOutlinedIcon />}
									label={courseCount}
								/>
							)}
							{linkCount > 0 && (
								<Chip
									sx={{
										height: 22,
										borderRadius: 1.25,
										"& .MuiChip-label": {
											px: 1,
											fontWeight: 700,
										},
									}}
									size="small"
									variant="outlined"
									icon={<LinkOutlinedIcon />}
									label={linkCount}
								/>
							)}
						</Box>

						{/* Edit action (reveals on hover) */}
						<Tooltip title={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.TrainingTab.PanelLeft.Accordion.TrainingFolderItem.L135C22.EditFolder", "Edit folder")} disableInteractive>
							<span>
								<IconButton
									className="folder-edit"
									size="small"
									disabled={folder.Id == null}
									onClick={(e: MouseEvent<HTMLButtonElement>) => {
										e.stopPropagation();
										setOpenFolder(true);
									}}
									sx={{ opacity: 0.6, "&:hover": { opacity: 1 } }}
								>
									<EditOutlinedIcon fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
					</Box>
				}
				sx={{
					// Subtle alternating background
					"& > .MuiTreeItem-content": {
						borderRadius: 1.5,
						backgroundColor: i % 2 === 0 ? evenBg : "transparent",
						transition: "background-color 120ms ease",
						"&:hover": {
							backgroundColor:
								i % 2 === 0
									? alpha(theme.palette.action.hover, 0.15)
									: theme.palette.action.hover,
						},
						// reveal edit button on row hover
						"&:hover .folder-edit": { opacity: 1 },
					},
					"& .MuiTreeItem-label": {
						width: "100%",
					},
				}}
			>
				{courses}
				{links}
			</TreeItem>

			<EditTrainingFolder
				handleClose={() => setOpenFolder(false)}
				open={openFolder}
				folder={folder}
			/>
		</>
	);
};
